import React, { useRef, useState } from 'react';
import { Item } from '../data/recommendationDataTypes.ts';
import createHeartComponent from '@finn-no/favorite-heart-component';
import PulseTracking from '../tracking/pulseTracking.tsx';
import { useTrackInscreen } from '../tracking/trackingHooks.ts';
import { getAdProperties } from './adHelpers.ts';
import { useAppConfig } from '../utils/AppConfig.tsx';
import ImageWrapper from './wrappers/ImageWrapper.tsx';
import LabelsWrapper from './wrappers/LabelsWrapper.tsx';

type AdProps = {
    item: Item;
    tracking: PulseTracking;
    inscreenTrackingIsEnabled: boolean;
};

const isBrowserRuntime = 'document' in globalThis;

function findIsolatedPodletRoot(): HTMLElement | null {
    if (!isBrowserRuntime) return null;
    return document.querySelector('recommendation-podlet-isolated')?.shadowRoot?.getElementById('recommendation-podlet') ?? null;
}

const Ad: React.FC<AdProps> = (props) => {
    const { item, tracking, inscreenTrackingIsEnabled } = props;
    const { urls, loginId, locale } = useAppConfig();
    const { cdnBaseUrl, favoriteBaseUrl } = urls;
    const domRef = useRef<HTMLDivElement>(null);

    const Heart = createHeartComponent({
        baseUri: favoriteBaseUrl,
        userId: loginId,
        renderServerSide: true,
        modalAttach: findIsolatedPodletRoot(),
    });

    const [hasBeenTracked, setHasBeenTracked] = useState(false);

    useTrackInscreen({
        tracking,
        ref: domRef,
        pulseContext: item?.tracking?.pulse,
        enabled: inscreenTrackingIsEnabled,
        hasBeenTracked,
        setHasBeenTracked,
    });

    const { itemIdNumber, shippingLabel, p4pLabel, easyapplyLabel, isCmsArticle, redirectUrl } = getAdProperties(item);

    const handleAdClick = (event) => {
        event.preventDefault();
        tracking.trackClickWithPulse(item.tracking?.pulse);
        setTimeout(() => {
            window.location.href = redirectUrl;
        }, 200);
    };
    return (
        <article className="relative isolate recommendation-ad card card--cardShadow s-bg" ref={domRef}>
            {item?.image?.url && <ImageWrapper item={item} cdnBaseUrl={cdnBaseUrl} />}

            <div className="p-12">
                <div className="text-s s-text-subtle">
                    <span className="nonEmpty">{item.location?.combined}</span>
                </div>

                <h2 className="t4 mb-0 break-words mt-6" id={item.itemId}>
                    <a
                        className="recommendation-ad-link s-text! hover:no-underline line-clamp"
                        href={redirectUrl}
                        id={item.itemId}
                        onClick={handleAdClick}>
                        <span className="absolute inset-0" aria-hidden="true"></span>
                        {item.heading}
                    </a>
                </h2>

                {/* company and custom labels */}
                <div className="text-s s-text-subtle mt-6">
                    {item.content?.company_name && <div>{item.content?.company_name}</div>}
                    {item.customLabel && <div>{item.customLabel}</div>}
                </div>
            </div>

            {!isCmsArticle && (
                <div className="absolute top-8 right-8">
                    {/* eslint-disable-next-line react-hooks/static-components */}
                    <Heart
                        variant="heart-on-image"
                        itemType="Ad"
                        // SAFETY: the podlet only configures supported FINN locales.
                        locale={locale as 'en' | 'nb' | 'fi'}
                        itemId={itemIdNumber}
                        isMobile={false}
                        renderNumFavs={false}
                    />
                </div>
            )}

            <LabelsWrapper shippingLabel={shippingLabel} easyapplyLabel={easyapplyLabel} p4pLabel={p4pLabel} isCmsArticle={isCmsArticle} />

            <div className="absolute inset-0 z-10 pointer-events-none outline-none rounded-8 border-2 border-transparent transition-all recommendation-ad-outline"></div>
        </article>
    );
};

export default Ad;
